import { LAYOUT_ANIMATION_DURATION, LAYOUT_ANIMATION_EASING } from '../../FlowchartView/hooks/flowchartLayout/constants';
import { sizeOf } from './nodeSize';
import { place } from './placeCamera';

const MOVE_EPS = 0.75;

export function positionChanges(fromMap, toPositions) {
  const changes = [];
  Object.entries(toPositions || {}).forEach(([taskId, position]) => {
    const old = fromMap?.get?.(taskId);
    if (!old || !position) return;
    if (Math.abs(old.x - position.x) < MOVE_EPS && Math.abs(old.y - position.y) < MOVE_EPS) return;
    changes.push({
      taskId,
      position: { x: position.x, y: position.y },
      oldPosition: { x: old.x, y: old.y },
    });
  });
  return changes;
}

/** Positions worth writing. Includes missions that have never been placed. */
export function persistablePositionChanges(tasks, toPositions) {
  const changes = [];
  (tasks || []).forEach((task) => {
    const position = toPositions?.[task?.id];
    if (!task?.id || !position || !Number.isFinite(position.x) || !Number.isFinite(position.y)) return;
    const had = Number.isFinite(task.x) && Number.isFinite(task.y);
    if (had && Math.abs(task.x - position.x) < MOVE_EPS && Math.abs(task.y - position.y) < MOVE_EPS) return;
    changes.push({
      taskId: task.id,
      position: { x: position.x, y: position.y },
      oldPosition: had ? { x: task.x, y: task.y } : null,
    });
  });
  return changes;
}

/**
 * Slide each card from where it is to the tidy spot. The wires and the
 * (+) buttons stay hidden until the cards arrive, so they don't sit on
 * the old places.
 * Returns a cancel function.
 */
export function slideBoard({
  world,
  wrap,
  from,
  to,
  cameraRef,
  focusId,
  tasks,
  boxes,
  onDone,
}) {
  const changes = positionChanges(from, to);
  const focus = focusId ? to?.[focusId] : null;
  if (!world || (changes.length === 0 && !focus)) {
    onDone?.();
    return () => {};
  }

  if (changes.length > 0) wrap?.classList.add('board-view--tidying');
  changes.forEach(({ taskId, position, oldPosition }) => {
    const dx = position.x - oldPosition.x;
    const dy = position.y - oldPosition.y;
    const el = world.querySelector(`[data-board-node="${taskId}"]`);
    if (el) {
      el.style.transition = `transform ${LAYOUT_ANIMATION_DURATION}ms ${LAYOUT_ANIMATION_EASING}`;
      el.style.transform = `translate(${dx}px, ${dy}px)`;
    }
    const origin = world.querySelector(`[data-board-origin="${taskId}"]`);
    if (origin) {
      origin.style.transition = `transform ${LAYOUT_ANIMATION_DURATION}ms ${LAYOUT_ANIMATION_EASING}`;
      origin.style.transform = `translate(${dx}px, ${dy}px)`;
    }
  });

  let frame = 0;
  const wrapEl = wrap;
  if (focus && wrapEl && cameraRef?.current) {
    const task = (tasks || []).find((item) => item.id === focusId);
    const size = sizeOf(task, boxes);
    const zoom = cameraRef.current.zoom || 1;
    const start = { ...cameraRef.current };
    const target = {
      zoom,
      x: wrapEl.clientWidth / 2 - (focus.x + size.width / 2) * zoom,
      y: wrapEl.clientHeight / 2 - (focus.y + size.height / 2) * zoom,
    };
    const ease = (t) => 1 - (1 - t) ** 3;
    let startTime = 0;
    const step = (now) => {
      if (!startTime) startTime = now;
      const progress = Math.min((now - startTime) / LAYOUT_ANIMATION_DURATION, 1);
      const eased = ease(progress);
      cameraRef.current = {
        zoom,
        x: start.x + (target.x - start.x) * eased,
        y: start.y + (target.y - start.y) * eased,
      };
      place(world, cameraRef.current);
      frame = progress < 1 ? requestAnimationFrame(step) : 0;
    };
    frame = requestAnimationFrame(step);
  }

  const clearSlide = () => {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    world.querySelectorAll('[data-board-node], [data-board-origin]').forEach((el) => {
      el.style.transition = 'none';
      el.style.removeProperty('transform');
    });
    wrap?.classList.remove('board-view--tidying');
    requestAnimationFrame(() => {
      world.querySelectorAll('[data-board-node], [data-board-origin]').forEach((el) => {
        if (el.style.transition === 'none') el.style.removeProperty('transition');
      });
    });
  };

  let finished = false;
  const timer = window.setTimeout(() => {
    if (finished) return;
    finished = true;
    onDone?.();
    clearSlide();
  }, LAYOUT_ANIMATION_DURATION + 40);

  return () => {
    if (finished) return;
    finished = true;
    window.clearTimeout(timer);
    clearSlide();
  };
}

/**
 * Leave FOKUS. Cards that were on screen slide back to their old spots.
 * The rest fade in already there. Both start together.
 * `from` is where the visible cards were. `to` is the whole board.
 */
export function playFokusReturn({
  world,
  wrap,
  from,
  to,
  cameraRef,
  cameraTo,
  onDone,
}) {
  if (!world) {
    onDone?.();
    return () => {};
  }

  const staying = [];
  const arriving = [];
  world.querySelectorAll('[data-board-node]').forEach((el) => {
    const id = el.getAttribute('data-board-node');
    const old = from?.get?.(id);
    const home = to?.[id];
    const moved = old && home
      && (Math.abs(old.x - home.x) >= MOVE_EPS || Math.abs(old.y - home.y) >= MOVE_EPS);
    if (moved) {
      el.style.transition = 'none';
      el.style.transform = `translate(${old.x - home.x}px, ${old.y - home.y}px)`;
      staying.push(el);
    } else if (!old) {
      el.style.transition = 'none';
      el.style.opacity = '0';
      arriving.push(el);
    }
  });

  wrap?.classList.add('board-view--tidying');
  world.getBoundingClientRect();

  let frame = 0;
  let finished = false;
  const clear = () => {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    world.querySelectorAll('[data-board-node]').forEach((el) => {
      el.style.transition = 'none';
      el.style.removeProperty('transform');
      el.style.removeProperty('opacity');
    });
    wrap?.classList.remove('board-view--tidying');
    requestAnimationFrame(() => {
      world.querySelectorAll('[data-board-node]').forEach((el) => {
        if (el.style.transition === 'none') el.style.removeProperty('transition');
      });
    });
  };

  const kick = requestAnimationFrame(() => {
    if (finished) return;
    staying.forEach((el) => {
      el.style.transition = `transform ${LAYOUT_ANIMATION_DURATION}ms ${LAYOUT_ANIMATION_EASING}`;
      el.style.transform = 'translate(0px, 0px)';
    });
    arriving.forEach((el) => {
      el.style.transition = `opacity ${LAYOUT_ANIMATION_DURATION}ms ease`;
      el.style.opacity = '1';
    });

    const start = cameraRef?.current ? { ...cameraRef.current } : null;
    if (!start || !cameraTo || !cameraRef) return;
    const ease = (t) => 1 - (1 - t) ** 3;
    let startTime = 0;
    const step = (now) => {
      if (finished) return;
      if (!startTime) startTime = now;
      const progress = Math.min((now - startTime) / LAYOUT_ANIMATION_DURATION, 1);
      const eased = ease(progress);
      cameraRef.current = {
        x: start.x + (cameraTo.x - start.x) * eased,
        y: start.y + (cameraTo.y - start.y) * eased,
        zoom: start.zoom + ((cameraTo.zoom || 1) - start.zoom) * eased,
      };
      place(world, cameraRef.current);
      frame = progress < 1 ? requestAnimationFrame(step) : 0;
    };
    frame = requestAnimationFrame(step);
  });

  const timer = window.setTimeout(() => {
    if (finished) return;
    finished = true;
    onDone?.();
    clear();
  }, LAYOUT_ANIMATION_DURATION + 40);

  return () => {
    if (finished) return;
    finished = true;
    cancelAnimationFrame(kick);
    window.clearTimeout(timer);
    clear();
  };
}
